/**
 * <s-product-gallery> — PDP main viewer with a thumbnail rail and prev/next arrows.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal scroll-snap track holding the slides.
 *   [data-gallery-slide]          — one per media item, direct children of the track.
 *   button[command="--prev|--next"][commandfor=<this id>] — arrow controls.
 *   button[command="--go"][data-index][commandfor=<this id>] — thumbnails.
 *   [data-gallery-status]         — polite live region for "Image n of m".
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;
  #announceRafId = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', this.#handleCommand, opts);

    const track = this.#track;
    const slides = this.#slides;
    if (!track || slides.length === 0) return;

    // Track which slide is visible so thumbnails and arrows follow swipes and scrolls too.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setIndex(slides.indexOf(entry.target), false);
          }
        }
      },
      { root: track, threshold: [0.6] },
    );
    slides.forEach((slide) => this.#observer.observe(slide));
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#announceRafId);
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #slides() {
    const track = this.#track;
    return track ? Array.from(track.querySelectorAll(':scope > [data-gallery-slide]')) : [];
  }

  #handleCommand = (event) => {
    const count = this.#slides.length;
    if (count === 0) return;

    if (event.command === '--next') {
      this.#goTo((this.#index + 1) % count);
    } else if (event.command === '--prev') {
      this.#goTo((this.#index - 1 + count) % count);
    } else if (event.command === '--go') {
      const target = Number(event.source?.dataset.index);
      if (Number.isInteger(target)) this.#goTo(target);
    }
  };

  #goTo(index) {
    const track = this.#track;
    const slide = this.#slides[index];
    if (!track || !slide) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const offset = slide.offsetLeft - track.offsetLeft;
    track.scrollTo({ left: isRtl ? offset - (track.scrollWidth - track.clientWidth) : offset, behavior: reduce ? 'auto' : 'smooth' });
    this.#setIndex(index, true);
  }

  #setIndex(index, announce) {
    if (index < 0 || index === this.#index) {
      if (announce) this.#announce();
      return;
    }
    this.#index = index;
    this.#sync();
    if (announce) this.#announce();
  }

  #sync() {
    const thumbs = this.querySelectorAll('[command="--go"][data-index]');
    thumbs.forEach((thumb) => {
      const active = Number(thumb.dataset.index) === this.#index;
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        // Keep the active thumbnail in view inside the rail without moving page focus.
        const rail = thumb.closest('[data-gallery-thumbs]');
        if (rail && rail.scrollHeight > rail.clientHeight) {
          const top = thumb.offsetTop - rail.offsetTop;
          if (top < rail.scrollTop || top + thumb.offsetHeight > rail.scrollTop + rail.clientHeight) {
            rail.scrollTo({ top: top - 8, behavior: 'auto' });
          }
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    this.#slides.forEach((slide, i) => {
      if (i === this.#index) {
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.inert = true;
      }
    });
  }

  #announce() {
    const status = this.querySelector('[data-gallery-status]');
    if (!status) return;
    const template = status.dataset.template || '';
    const text = template.replace('[current]', String(this.#index + 1)).replace('[total]', String(this.#slides.length));
    cancelAnimationFrame(this.#announceRafId);
    status.textContent = '';
    this.#announceRafId = requestAnimationFrame(() => {
      status.textContent = text;
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
